import './ChangeContactDetailsModal.scss';
import React, { useState } from 'react';
import * as yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, FormGroup, Label, InputGroup } from 'reactstrap';
import InputMask from 'react-input-mask';
import { Form, Field } from '@availity/form';
import { useStateSelector } from '../../../store/selectors';
import { COMMON_REGEX } from '../../../utils/constants';
import Helpers from '../../../utils/helper';
import Rodal from '../../Rodal/Rodal';
import createNotification from '../../../utils/createNotification';
import { ModalBody, ModalHeader } from 'reactstrap';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';

interface ChangeContactDetailsModalProps {
    isVisible: boolean;
    setIsVisible: (isVisible: boolean) => void;
    phoneNumber: string;
    email: string;
    getSummaryData(): void;
}

const ChangeContactDetailsModal = (props: ChangeContactDetailsModalProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [phoneNumber, setPhoneNumber] = useState(props.phoneNumber);
    const [email, setEmail] = useState(props.email);

    const [showEmailVerifyCode, setShowEmailVerifyCode] = useState(false);
    const [showPhoneVerifyCode, setShowPhoneVerifyCode] = useState(false);
    const [emailVerifyCode, setEmailVerifyCode] = useState('');
    const [phoneVerifyCode, setPhoneVerifyCode] = useState('');

    const [phoneNumberInvalid, setPhoneNumberInvalid] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const phoneNumberOnBlur = (phone: string) => {
        setPhoneNumberInvalid(
            phone && phone.replace(/[-() ]/g, '').length === 10 ? false : true
        );
    };

    const updateDetails = (updateField: 'phone' | 'email') => {
        if (!phoneNumber || !email || phoneNumberInvalid) {
            return;
        }

        setIsLoading(true);

        const requestObj = {
            phoneNumber:
                updateField === 'phone'
                    ? Helpers.trimPhoneNumberFromMask(phoneNumber)
                    : '',
            email: updateField === 'email' ? email : '',
        };

        axios
            .put('/api/Users', requestObj)
            .then((response: any) => {
                if (response.data) {
                    if (!response.data.isError) {
                        createNotification(
                            'Contact details update request initiated. Please refer your email or phone for the verification code.',
                            'success'
                        );
                        updateField === 'phone'
                            ? setShowPhoneVerifyCode(true)
                            : setShowEmailVerifyCode(true);
                    } else {
                        createNotification(response.data.message, 'error');
                    }
                } else {
                    const message = response.response.data.message;
                    createNotification(message, 'error');
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const verifyUserDetails = (updateField: 'phone' | 'email') => {
        if (updateField === 'phone') {
            if (!phoneVerifyCode || !phoneNumber) {
                return;
            }
        }

        if (updateField === 'email') {
            if (!emailVerifyCode || !email) {
                return;
            }
        }

        setIsLoading(true);

        const requestObj = {
            phone:
                updateField === 'phone'
                    ? Helpers.trimPhoneNumberFromMask(phoneNumber)
                    : '',
            email: updateField === 'email' ? email : '',
            verificationCode:
                updateField === 'phone' ? phoneVerifyCode : emailVerifyCode,
        };

        axios
            .post('/api/Users/UpdateUserVerify', requestObj)
            .then((response: any) => {
                if (response.data) {
                    if (!response.data.isError) {
                        createNotification(
                            'Contact details updated successfully.',
                            'success'
                        );
                        props.setIsVisible(false);
                        props.getSummaryData();
                        updateField === 'phone'
                            ? setShowPhoneVerifyCode(false)
                            : setShowEmailVerifyCode(false);
                    } else {
                        createNotification(response.data.message, 'error');
                    }
                } else {
                    const message = response.response.data.message;
                    createNotification(message, 'error');
                }
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div>
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={() => props.setIsVisible(false)}
                showMask={true}
                width={600}
                className="change-contact-details-popup"
                center>
                <ModalHeader>Change Contact Details</ModalHeader>
                <ModalBody>
                    <Form
                        onSubmit={() => {}}
                        initialValues={{}}
                        validationSchema={yup.object().shape({
                            email: yup
                                .string()
                                .required('Email is required')
                                .matches(COMMON_REGEX.EMAIL, {
                                    message: 'Please enter a valid Email Id',
                                }),
                            emailVerify: yup.string().when('sentEmailRequest', {
                                is: () => showEmailVerifyCode,
                                then: () => yup.string().required(),
                            }),
                            phoneNumber: yup.string().required(),
                            phoneVerify: yup.string().when('sentPhoneRequest', {
                                is: () => showPhoneVerifyCode,
                                then: () => yup.string().required(),
                            }),
                        })}>
                        <div className="card-body">
                            <div className="form-wizard-content">
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="txtphoneNumber">
                                                Phone Number
                                            </Label>
                                            <InputGroup
                                                className={`mb-3${
                                                    phoneNumberInvalid
                                                        ? ' invalid-phone'
                                                        : ''
                                                }`}>
                                                <div className="input-group-text prepend-addon">
                                                    <FontAwesomeIcon
                                                        icon={faPhone}
                                                    />
                                                </div>
                                                <InputMask
                                                    required
                                                    className="form-control"
                                                    placeholder="(999) 999-9999"
                                                    mask="(999) 999-9999"
                                                    maskChar={null}
                                                    name="phoneNumber"
                                                    id="txtphoneNumber"
                                                    value={phoneNumber}
                                                    onChange={(event) => {
                                                        setPhoneNumberInvalid(
                                                            event.target
                                                                .value &&
                                                                event.target.value.replace(
                                                                    /[-() ]/g,
                                                                    ''
                                                                ).length === 10
                                                                ? false
                                                                : true
                                                        );
                                                        setPhoneNumber(
                                                            event.target.value
                                                        );
                                                    }}
                                                    onBlur={(event) =>
                                                        phoneNumberOnBlur(
                                                            event?.target?.value
                                                        )
                                                    }
                                                />
                                            </InputGroup>
                                        </FormGroup>
                                        {showPhoneVerifyCode ? (
                                            <FormGroup>
                                                <Label for="txtVerifyPhone">
                                                    Verify New Phone
                                                </Label>
                                                <Field
                                                    type="text"
                                                    placeholder="Enter verification code received on text"
                                                    name="phoneVerify"
                                                    id="txtVerifyPhone"
                                                    onChange={(event) =>
                                                        setPhoneVerifyCode(
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </FormGroup>
                                        ) : (
                                            ''
                                        )}
                                        <ButtonLoader
                                            disabled={isLoading}
                                            isLoading={isLoading}
                                            className="btn btn-success btn-lg"
                                            buttonText={
                                                showPhoneVerifyCode
                                                    ? 'Verify'
                                                    : 'Update'
                                            }
                                            loaderButtonText={
                                                showPhoneVerifyCode
                                                    ? 'Verify'
                                                    : 'Update'
                                            }
                                            onClick={() => {
                                                showPhoneVerifyCode
                                                    ? verifyUserDetails('phone')
                                                    : updateDetails('phone');
                                            }}
                                        />
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="txtEmail">Email</Label>
                                            <Field
                                                type="text"
                                                value={email}
                                                placeholder="Enter email"
                                                name="email"
                                                id="txtEmail"
                                                onChange={(event) =>
                                                    setEmail(event.target.value)
                                                }
                                            />
                                        </FormGroup>

                                        {showEmailVerifyCode ? (
                                            <FormGroup>
                                                <Label for="txtVerifyEmail">
                                                    Verify New Email
                                                </Label>
                                                <Field
                                                    type="text"
                                                    placeholder="Enter verification code received on email"
                                                    name="emailVerify"
                                                    id="txtVerifyEmail"
                                                    onChange={(event) =>
                                                        setEmailVerifyCode(
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </FormGroup>
                                        ) : (
                                            ''
                                        )}
                                        <ButtonLoader
                                            disabled={isLoading}
                                            isLoading={isLoading}
                                            className="btn btn-success btn-lg"
                                            buttonText={
                                                showEmailVerifyCode
                                                    ? 'Verify'
                                                    : 'Update'
                                            }
                                            loaderButtonText={
                                                showEmailVerifyCode
                                                    ? 'Verify'
                                                    : 'Update'
                                            }
                                            onClick={() => {
                                                showEmailVerifyCode
                                                    ? verifyUserDetails('email')
                                                    : updateDetails('email');
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="d-block text-right card-footer">
                            <button
                                className="mr-2 btn btn-primary btn-lg"
                                onClick={() => props.setIsVisible(false)}>
                                Cancel
                            </button>
                        </div>
                    </Form>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default ChangeContactDetailsModal;
